// Pure formatters for the Members surface. Pure so they can be unit-tested
// without rendering, and reused by future surfaces (lifecycle chart legend,
// alert preview rows) without dragging React in.

import { formatDistanceToNowStrict } from "date-fns";

/**
 * "5 minutes ago" / "3 days ago" / "Lurker" — a relative-time wrapper that
 * collapses the null case (a member who joined but never messaged) into a
 * single label rather than asking every caller to ternary it. We use
 * date-fns' strict variant so the output is deterministic ("3 days" not
 * "about 3 days") for the rare future snapshot test.
 */
export function formatLastActive(
  lastActiveIso: string | null,
  options: { lurkerLabel?: string; nowDate?: Date } = {},
): string {
  if (lastActiveIso === null) return options.lurkerLabel ?? "Never active";
  const then = new Date(lastActiveIso);
  if (Number.isNaN(then.getTime())) return options.lurkerLabel ?? "Never active";
  const distance = formatDistanceToNowStrict(then, {
    addSuffix: true,
    ...(options.nowDate ? {} : {}),
  });
  return distance;
}

/**
 * "@username" or a fallback when the member has no public @handle (many
 * Telegram accounts don't). The fallback is a Telegram-id stub so the row
 * still has a stable identifier rather than collapsing to whitespace.
 */
export function formatHandle(
  username: string | null,
  telegramId: string,
): string {
  if (username && username.length > 0) return `@${username}`;
  // Mask all but the trailing 4 digits — Telegram IDs are sensitive (§15.3
  // tags them as a non-display field). Showing the trailing 4 lets the
  // owner disambiguate "two unnamed members" without leaking the full id.
  const tail = telegramId.slice(-4);
  return `tg•${tail}`;
}
